/**
 * Utility functions for handling dates in the application,
 * including special handling for Java date arrays
 */

/**
 * Converts a Java date array [year, month, day] to a JavaScript date string
 * Note: In Java month is 1-based, in JavaScript it's 0-based
 * @param dateArray Array in format [year, month, day]
 * @returns ISO formatted date string (YYYY-MM-DD)
 */
export function convertJavaDateArrayToString(dateArray) {
  if (!dateArray || !Array.isArray(dateArray) || dateArray.length < 3) {
    return '';
  }
  
  // Extract year, month, day from the array
  const [year, month, day] = dateArray;
  
  // Create a date object (adjusting month from 1-based to 0-based)
  const date = new Date(year, month - 1, day);
  
  // Return an ISO format date string (YYYY-MM-DD)
  return date.toISOString().split('T')[0];
}

/**
 * Converts any date value (string or Java array) to a format suitable for v-date-picker
 * @param dateValue Date value (string, array, or null/undefined)
 * @returns Formatted date string (YYYY-MM-DD) or empty string
 */
export function formatDateForPicker(dateValue) {
  if (!dateValue) return '';
  
  if (Array.isArray(dateValue)) {
    return convertJavaDateArrayToString(dateValue);
  } 
  
  // If it's already a string, ensure it's in YYYY-MM-DD format
  if (typeof dateValue === 'string') {
    // Handle ISO format by extracting just the date part
    if (dateValue.includes('T')) {
      return dateValue.split('T')[0];
    }
    return dateValue;
  }
  
  return '';
}

/**
 * Formats a date value for display in the UI, handling various formats
 * @param dateValue Date value in any format (string, array, Date object)
 * @returns Localized date string for display
 */
export function formatDateForDisplay(dateValue) {
  if (!dateValue) return '';
  
  try {
    let date;
    
    // Check if the date is in Java array format [year, month, day]
    if (Array.isArray(dateValue) && dateValue.length >= 3) {
      const [year, month, day] = dateValue;
      // Note: Java month is 1-based, JavaScript month is 0-based
      date = new Date(year, month - 1, day);
    } else if (dateValue instanceof Date) {
      date = dateValue;
    } else {
      // Handle as regular string date
      date = new Date(dateValue);
    }
    
    // Check if date is valid
    if (isNaN(date.getTime())) {
      console.warn('Invalid date value:', dateValue);
      return String(dateValue);
    }
    
    return date.toLocaleDateString();
  } catch (error) {
    console.error('Error formatting date:', error);
    return String(dateValue);
  }
}

/**
 * Converts a string date to a Java date array format [year, month, day]
 * Useful when sending dates back to a Java backend
 * @param dateString ISO format date string (YYYY-MM-DD)
 * @returns Array in format [year, month, day] where month is 1-based
 */
export function convertStringToJavaDateArray(dateString) {
  if (!dateString) return null;
  
  try {
    const date = new Date(dateString);
    
    // Check if date is valid
    if (isNaN(date.getTime())) {
      return null;
    }
    
    // Create array with Java-style 1-based month
    return [
      date.getFullYear(),
      date.getMonth() + 1, // Convert to 1-based month
      date.getDate()
    ];
  } catch (error) {
    console.error('Error converting date to Java array:', error);
    return null;
  }
}

/**
 * Parses any date format and returns a JavaScript Date object
 * @param dateValue Date in any format (string, array, Date object)
 * @returns JavaScript Date object or null if invalid
 */
export function parseDate(dateValue) {
  if (!dateValue) return null;
  
  try {
    // Handle Java array format
    if (Array.isArray(dateValue) && dateValue.length >= 3) {
      const [year, month, day] = dateValue;
      return new Date(year, month - 1, day);
    }
    
    // Already a Date object
    if (dateValue instanceof Date) {
      return dateValue;
    }
    
    // Handle string format
    const date = new Date(dateValue);
    
    // Check if date is valid
    if (isNaN(date.getTime())) {
      return null;
    }
    
    return date;
  } catch (error) {
    console.error('Error parsing date:', error);
    return null;
  }
}